import type { FeeStructure } from "../../services/financialService";
import { feeCategoryLabel } from "./feeCatalogue";
import { audienceNote, structureBilling } from "./feeStructures";

/** What a fee structure contains and what each kind of learner is billed from it. */
export function FeeStructureSummary({ structure, termName }: { structure: FeeStructure; termName: string }) {
  const billing = structureBilling(structure);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="rounded-full bg-primary-50 px-2.5 py-1 font-medium text-primary-700">{structure.level}</span>
        <span className="rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-700">{termName}</span>
        {!structure.is_active && <span className="rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-500">Inactive</span>}
        {structure.pending_invoice_count > 0 ? (
          <span className="rounded-full bg-amber-50 px-2.5 py-1 font-medium text-amber-700">
            {structure.pending_invoice_count} student{structure.pending_invoice_count === 1 ? "" : "s"} not yet invoiced
          </span>
        ) : (
          <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-700">Every student invoiced</span>
        )}
      </div>

      <section aria-labelledby="structure-billing-heading">
        <h4 id="structure-billing-heading" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Billed per student
        </h4>
        <div className={`mt-2 grid gap-2 ${billing.cases.length > 1 ? "sm:grid-cols-2" : ""}`}>
          {billing.cases.map((row) => (
            <div key={row.label} className="rounded-lg bg-primary-50 px-4 py-3">
              <p className="text-xs font-medium text-primary-700">{row.label}</p>
              <p className="mt-0.5 text-lg font-semibold tabular-nums text-gray-900">UGX {row.amount.toLocaleString()}</p>
            </div>
          ))}
        </div>
      </section>

      <section aria-labelledby="structure-items-heading">
        <h4 id="structure-items-heading" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Fee items ({structure.items.length})
        </h4>
        <div className="sheet-wrap mt-2 max-h-none">
          <table className="sheet">
            <thead>
              <tr>
                <th>Fee</th>
                <th>Category</th>
                <th>Who pays</th>
                <th>Terms</th>
                <th className="text-right">Amount (UGX)</th>
              </tr>
            </thead>
            <tbody>
              {structure.items.map((item, index) => (
                <tr key={item.id ?? index}>
                  <td className="font-medium text-gray-900">{item.fee_name}</td>
                  <td className="text-gray-700">{item.fee_type ? feeCategoryLabel(item.fee_type) : "—"}</td>
                  <td className="text-gray-700">{audienceNote(item)}</td>
                  <td className="text-gray-700">{item.allows_partial_payment === false ? "Pay in full" : "Installments"}</td>
                  <td className="text-right tabular-nums text-gray-900">{Number(item.amount).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {structure.notes?.trim() && (
        <section aria-labelledby="structure-notes-heading">
          <h4 id="structure-notes-heading" className="text-xs font-semibold uppercase tracking-wide text-gray-500">Notes</h4>
          <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-6 text-gray-800">{structure.notes}</p>
        </section>
      )}
    </div>
  );
}
